import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MessageCircle, Phone, Moon, Eye, Sparkles, Star, Loader2 } from 'lucide-react';
import { trainingModules } from '@/data/trainingData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Training Access Screen (for purchased users - manual activation)
const TrainingAccessScreen = ({ onBack }) => {
  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="training-access-screen">
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-slate-400 hover:text-slate-200 transition-colors"
            data-testid="training-back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-heading text-slate-100">
            Eğitim Erişimi
          </h1>
        </div>
        
        {/* Main Content */}
        <div className="glass-panel p-8 md:p-12 space-y-8 text-center border-green-500/20">
          <div className="flex justify-center">
            <div 
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ 
                backgroundColor: 'rgba(34, 197, 94, 0.2)',
                boxShadow: '0 0 30px rgba(34, 197, 94, 0.3)'
              }}
            >
              <Sparkles className="w-10 h-10 text-green-400" />
            </div>
          </div>
          
          <div className="space-y-4">
            <h2 className="text-2xl font-heading text-slate-100">
              Satın Alma Başarılı
            </h2>
            <p className="text-slate-400 leading-relaxed max-w-lg mx-auto">
              Eğitim videoları şu anda sistemsel olarak yayına hazırlanmaktadır.
            </p>
          </div>
          
          <div className="glass-panel p-6 bg-violet-500/5 border-violet-500/20 space-y-4">
            <p className="text-slate-300 leading-relaxed">
              <strong className="text-violet-400">Satın aldıysanız</strong>, erişim aktivasyonu için 
              WhatsApp üzerinden bizimle iletişime geçebilirsiniz:
            </p>
            
            <a
              href="https://wa.me/905539887117"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-full px-8 py-4 text-green-400 transition-all"
              data-testid="whatsapp-contact-btn"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="font-medium">WhatsApp ile İletişim</span>
            </a>
            
            <div className="flex items-center justify-center gap-2 text-slate-400">
              <Phone className="w-4 h-4" />
              <span className="font-mono">+90 553 988 71 17</span>
            </div>
          </div>
          
          <p className="text-sm text-slate-500">
            Genellikle <strong className="text-slate-400">aynı gün içinde</strong> erişiminiz açılır.
          </p>
        </div>
      </div>
    </div>
  );
};

// Training Sales Screen (for non-purchased users)
const TrainingSalesScreen = ({ onBack }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [error, setError] = useState('');
  
  const handlePurchase = async (packageId) => {
    setIsProcessing(true);
    setProcessingPlan(packageId);
    setError('');
    
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${BACKEND_URL}/api/payments/checkout`, {
        package_id: packageId,
        origin_url: originUrl
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('Ödeme başlatılırken bir hata oluştu. Lütfen tekrar deneyin.');
      setIsProcessing(false);
      setProcessingPlan(null);
    }
  };
  
  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="training-sales-screen">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-slate-400 hover:text-slate-200 transition-colors"
            data-testid="training-back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-mystic text-slate-100">
              Durugörü Eğitim Programı
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Sezgisel yeteneklerinizi geliştirin
            </p>
          </div>
        </div>
        
        {/* Hero Section with Mystical Icons */}
        <div className="glass-panel p-8 text-center space-y-6 border-violet-500/20 bg-gradient-to-br from-violet-500/5 to-transparent relative overflow-hidden">
          {/* Mystical decorations */}
          <div className="absolute top-4 right-4 opacity-20">
            <Moon className="w-8 h-8 text-violet-400" />
          </div>
          <div className="absolute bottom-4 left-4 opacity-20">
            <Star className="w-6 h-6 text-cyan-400" />
          </div>
          
          <div className="flex justify-center">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ 
                backgroundColor: 'rgba(139, 92, 246, 0.2)',
                boxShadow: '0 0 30px rgba(139, 92, 246, 0.4)'
              }}
            >
              <Eye className="w-8 h-8 text-violet-400" />
            </div>
          </div>
          
          <h2 className="text-xl md:text-2xl font-heading text-slate-100 leading-tight">
            Üçüncü Gözünüzü Açın,<br/>
            <span className="text-violet-400">Sezgilerinizi Güçlendirin</span>
          </h2>
          <p className="text-slate-400 leading-relaxed max-w-xl mx-auto text-sm">
            Video dersler, pratik egzersizler ve kişisel gelişim rehberleri ile 
            sezgisel yeteneklerinizi sistematik olarak geliştirin.
          </p>
        </div>
        
        {/* Pricing - Direct display */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Monthly */}
          <button
            onClick={() => handlePurchase('training_monthly')}
            disabled={isProcessing}
            className="glass-panel p-6 space-y-4 text-left hover:border-violet-500/30 transition-all disabled:opacity-50 relative overflow-hidden group"
            data-testid="monthly-plan-btn"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            
            <div className="relative">
              <p className="text-xs tracking-widest uppercase text-cyan-400 mb-2">Aylık Abonelik</p>
              <div className="flex items-baseline gap-1">
                <p className="text-4xl font-heading text-slate-100">₺299</p>
                <span className="text-slate-500 text-sm">/ay</span>
              </div>
            </div>
            
            <div className="relative text-sm text-slate-400 space-y-2 pt-4 border-t border-white/10">
              <p className="flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-cyan-400" />
                Tüm eğitim videolarına erişim
              </p>
              <p className="flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-cyan-400" />
                1 aylık erişim
              </p>
              <p className="flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-cyan-400" />
                İstediğiniz zaman iptal
              </p>
            </div>
            
            {isProcessing && processingPlan === 'training_monthly' && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
              </div>
            )}
          </button>
          
          {/* Yearly - Recommended */}
          <button
            onClick={() => handlePurchase('training_yearly')}
            disabled={isProcessing}
            className="glass-panel p-6 space-y-4 border-violet-500/30 text-left hover:border-violet-500/50 transition-all disabled:opacity-50 relative overflow-hidden group"
            data-testid="yearly-plan-btn"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 to-transparent" />
            
            {/* Recommended badge */}
            <div className="absolute -top-1 -right-1">
              <div className="px-3 py-1 bg-violet-500/30 text-violet-300 text-xs rounded-bl-lg border-l border-b border-violet-500/30">
                ⭐ Önerilen
              </div>
            </div>
            
            <div className="relative">
              <p className="text-xs tracking-widest uppercase text-violet-400 mb-2">Yıllık Abonelik</p>
              <div className="flex items-baseline gap-1">
                <p className="text-4xl font-heading text-violet-400">₺1.999</p>
                <span className="text-slate-500 text-sm">/yıl</span>
              </div>
              <p className="text-xs text-green-400 mt-1">5+ ay ücretsiz!</p>
            </div>
            
            <div className="relative text-sm text-slate-400 space-y-2 pt-4 border-t border-white/10">
              <p className="flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-violet-400" />
                Tüm eğitim videolarına erişim
              </p>
              <p className="flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-violet-400" />
                1 yıllık erişim
              </p>
              <p className="flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-violet-400" />
                ₺1.589 tasarruf
              </p>
            </div>
            
            {isProcessing && processingPlan === 'training_yearly' && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
              </div>
            )}
          </button>
        </div>
        
        {/* Error message */}
        {error && (
          <div className="glass-panel p-4 bg-red-500/10 border-red-500/30">
            <p className="text-red-400 text-sm text-center">{error}</p>
          </div>
        )}
        
        {/* Module Preview */}
        <div className="space-y-4">
          <h3 className="text-sm tracking-widest uppercase text-violet-400 font-medium flex items-center gap-2">
            <Moon className="w-4 h-4" />
            Eğitim Modülleri
          </h3>
          
          <div className="grid gap-3">
            {trainingModules.map((module) => (
              <div
                key={module.id}
                className="glass-panel p-4 flex items-start gap-4"
              >
                <div 
                  className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-heading"
                  style={{ 
                    backgroundColor: `rgba(139, 92, 246, ${0.1 + module.id * 0.05})`,
                    color: module.id >= 4 ? '#f472b6' : module.id >= 2 ? '#a78bfa' : '#22d3ee'
                  }}
                >
                  {module.id}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-heading text-slate-100 text-sm">
                    {module.title}
                  </h4>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                    {module.description}
                  </p>
                </div>
                <span className="text-xs text-slate-600 whitespace-nowrap">
                  {module.duration}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Disclaimer */}
        <div className="text-center pt-4">
          <p className="text-xs text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Bu program, kişisel gelişim ve algısal eğitim için tasarlanmıştır. 
            Teşhis, tahmin veya garanti sağlamaz.
          </p>
        </div>
      </div>
    </div>
  );
};

// Main Training Dashboard Component
export const TrainingDashboard = ({ onBack, onOpenModule, onStartSession }) => {
  const [isPremium] = useState(() => localStorage.getItem('zenermind_is_premium') === 'true');
  
  // If premium, show access screen with WhatsApp contact
  if (isPremium) {
    return <TrainingAccessScreen onBack={onBack} />;
  }
  
  // Show sales screen for non-premium users
  return <TrainingSalesScreen onBack={onBack} />;
};
